import React, { Suspense, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Outlet } from "@tanstack/react-router"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { Toasts } from "@/components/Toasts"
import { usePageUnload } from "@/utils/usePreventPageUnload"
import { usePreventPageZoom } from "@/utils/usePreventPageZoom"
import { DialogManager } from "@/components/DialogManager"
import { showingSplashAtom } from "@/store/ui-views-state"
import { initializeApp } from "@/store/initialize-app"
import { CatalogManager } from "@/lib/catalog"
import { ContextMenuManager } from "@/components/ContextMenuManager"
import { Icon } from "./icons/Icon"
import { Button } from "@/components/ui/Button"
import { criticalFailureAtom, sessionCountAtom } from "@/store/app-state"
import { AppAuthGuard } from "./AppAuthGuard"
import { AppWhitelistGuard } from "./AppWhitelistGuard"
import logoWithTextWhiteUrl from "@assets/logo_with_text_white.png?url"
import logoPixiesetWhiteUrl from "@assets/logo_pixieset_white.png?url"
import { dialogsAtom } from "@/store/dialogs-state"
import { DialogInert } from "@/components/Dialogs/DialogInert"
import { analytics } from "@/telemetry/amplitude"
import { ElectronIPC } from "./electron/ElectronIPC"
import { GPUAssessment } from "./GPUAssessment"
import { Sentry } from "@/telemetry/sentry"
import { useAuth } from "@/utils/auth"
import { useProjectRouteLifecycle } from "@/routing/useProjectRouteLifecycle"
import { useProjectBeforeUnload } from "@/routing/useProjectBeforeUnload"
import { useRouteUIStateSync } from "@/routing/useRouteUiStateSync"
import { userQueryAtom } from "@/api/users-api"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { NetworkStatusBanner } from "@/components/NetworkStatusBanner"
import { UpcomingMaintenanceNotification } from "@/components/UpcomingMaintenanceNotification"
import {
	editorBackgroundAtom,
	isI18nLanguage,
	languageAtom,
	themeModeAtom,
	usePreferredLocaleAtom,
} from "@/store/settings-state"
import { changeLanguageAtom, changeRuntimeLanguage } from "@/store/localization-state"
import {
	applyThemeMode,
	getEditorBackgroundForTheme,
	getSystemThemeMediaQuery,
} from "@/utils/theme-mode"
import { isElectronApp, setElectronShellLocale } from "@/utils/electron"
import { getEnabledLocales } from "@/utils/localization-access"
import { useAppConfigQuery } from "@/api/app-config-api"

const SplashScreen: React.FC = () => {
	const { t } = useTranslation("components")
	const { isAuthenticated, isLoading } = useAuth()
	const setShowSplash = useSetAtom(showingSplashAtom)

	if (isElectronApp && !(isLoading || isAuthenticated)) {
		return null
	}

	return (
		<div
			className="absolute left-0 top-0 z-50 flex size-full items-center justify-center bg-base-900"
			style={{
				animationName: "fadeOut",
				animationDuration: "1.5s",
				animationFillMode: "forwards",
			}}
			onAnimationEnd={(e) => {
				if (e.animationName === "fadeOut") setShowSplash(false)
			}}
		>
			<img
				className="h-6 invert dark:invert-0"
				src={isPixiesetAuthEnabled() ? logoPixiesetWhiteUrl : logoWithTextWhiteUrl}
				alt={t("app.altLogo")}
				style={{
					animationName: "popIn",
					animationDuration: "0.5s",
					animationFillMode: "forwards",
				}}
			/>
		</div>
	)
}

const ProcessingFailureScreen: React.FC = () => {
	const { t } = useTranslation("components")
	return (
		<div className="electron-drag absolute inset-0 z-50 flex flex-col items-center justify-center space-y-4 bg-base-900/75 backdrop-blur">
			<p>
				<Icon className="text-red-800" width="64" height="64" name="error" />
			</p>
			<p className="w-3/6 text-center">
				{t("app.criticalError")}
				<br />
				{t("app.pleaseReload")}
			</p>
			<Button
				className="electron-no-drag"
				variant="primary"
				trackingLabel="reload"
				onClick={() => window.location.reload()}
			>
				{t("app.reload")}
			</Button>
		</div>
	)
}

function App() {
	const [hasCriticalFailure] = useAtom(criticalFailureAtom)
	const showSplash = useAtomValue(showingSplashAtom)
	const [dialogs] = useAtomValue(dialogsAtom)
	const themeMode = useAtomValue(themeModeAtom)
	const setEditorBackground = useSetAtom(editorBackgroundAtom)
	const currentLanguage = useAtomValue(languageAtom)
	const usePreferredLocale = useAtomValue(usePreferredLocaleAtom)
	const changeLanguage = useSetAtom(changeLanguageAtom)
	const appConfigQuery = useAppConfigQuery()
	const enabledLocales = React.useMemo(
		() => getEnabledLocales(appConfigQuery.data),
		[appConfigQuery.data],
	)

	const setSessionCount = useSetAtom(sessionCountAtom)
	usePageUnload(() => CatalogManager.unlockProject())
	const userQuery = useAtomValue(userQueryAtom)
	const sessionCountIncrementedRef = React.useRef(false)

	usePreventPageZoom()
	useProjectRouteLifecycle()
	useProjectBeforeUnload()
	useRouteUIStateSync()

	useEffect(() => {
		initializeApp().catch(Sentry.captureException)
		analytics.trackEvent({ label: "app_launched", type: "event" })
	}, [])

	// Increment session count when user is successfully loaded (only once per app launch)
	useEffect(() => {
		if (userQuery.data && !sessionCountIncrementedRef.current) {
			sessionCountIncrementedRef.current = true
			setSessionCount((prev) => prev + 1).catch(Sentry.captureException)
		} else if (!userQuery.data && sessionCountIncrementedRef.current) {
			// Reset flag when user logs out, so next login will increment again
			sessionCountIncrementedRef.current = false
		}
	}, [setSessionCount, userQuery.data])

	useEffect(() => {
		if (!userQuery.data) {
			return
		}

		const preferredLocale = userQuery.data?.preferredLocale
		const requestedLocale = usePreferredLocale
			? preferredLocale && isI18nLanguage(preferredLocale)
				? preferredLocale
				: "en"
			: currentLanguage

		const languagePromise = enabledLocales.includes(requestedLocale)
			? usePreferredLocale && requestedLocale !== currentLanguage
				? changeLanguage(requestedLocale)
				: changeRuntimeLanguage(requestedLocale)
			: changeRuntimeLanguage("en")

		void languagePromise
			.then((appliedLocale: unknown) =>
				typeof appliedLocale === "string" && isI18nLanguage(appliedLocale)
					? setElectronShellLocale(appliedLocale)
					: undefined,
			)
			.catch(Sentry.captureException)
	}, [
		changeLanguage,
		currentLanguage,
		enabledLocales,
		usePreferredLocale,
		userQuery.data,
		userQuery.data?.preferredLocale,
	])

	useEffect(() => {
		const handleContextMenu = (e: Event) => {
			// Allow synthetic contextmenu events used by in-app menus (Radix trigger).
			if (!e.isTrusted) {
				return
			}
			e.preventDefault()
		}

		document.addEventListener("contextmenu", handleContextMenu, { capture: true })
		window.addEventListener("contextmenu", handleContextMenu, { capture: true })

		return () => {
			document.removeEventListener("contextmenu", handleContextMenu, { capture: true })
			window.removeEventListener("contextmenu", handleContextMenu, { capture: true })
		}
	}, [])

	useEffect(() => {
		const mediaQuery = getSystemThemeMediaQuery()
		const resolvedTheme = applyThemeMode(themeMode, mediaQuery?.matches)

		if (themeMode === "system") {
			setEditorBackground(getEditorBackgroundForTheme(resolvedTheme))
		}

		if (themeMode !== "system" || !mediaQuery) {
			return
		}

		const handleChange = (event: MediaQueryListEvent) => {
			const nextTheme = applyThemeMode("system", event.matches)
			setEditorBackground(getEditorBackgroundForTheme(nextTheme))
		}

		if (typeof mediaQuery.addEventListener === "function") {
			mediaQuery.addEventListener("change", handleChange)
			return () => mediaQuery.removeEventListener("change", handleChange)
		}

		mediaQuery.addListener(handleChange)
		return () => mediaQuery.removeListener(handleChange)
	}, [setEditorBackground, themeMode])

	return (
		<ElectronIPC>
			<NetworkStatusBanner />
			{showSplash && <SplashScreen key="splash" />}
			<div key="root" className="flex size-full overflow-hidden">
				<DialogInert>
					<AppAuthGuard>
						<UpcomingMaintenanceNotification />
						<AppWhitelistGuard>
							<Suspense>
								<GPUAssessment>
									<Outlet />
								</GPUAssessment>
							</Suspense>
						</AppWhitelistGuard>
					</AppAuthGuard>
					<Toasts />
				</DialogInert>
				{dialogs && dialogs.length > 0 && <DialogManager />}
				<ContextMenuManager />
			</div>
			{hasCriticalFailure ? <ProcessingFailureScreen key="processing-failure" /> : ""}
		</ElectronIPC>
	)
}

export default App
